// Jhandi Munda is a traditional Nepali dice betting game.
// It is played with six, six-sided dice. The symbols on the faces of the dice are Spade, Club, Diamond, Heart, Crown and Flag.
// Players place bets on which symbol they think will appear the most number of times after the dice are rolled.
// The player who correctly guesses the symbol that appears the most wins.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import DiceScene from './DiceScene.js';
import SimpleDiceRoll from './SimpleDiceRoll.js';
import './Game.css';
import { achievements } from './achievements';
import './Achievements.css';

// The six symbols used in the game
const symbols = ['heart', 'crown', 'spade', 'diamond', 'flag', 'club'];
const symbolIcons = {
    heart: '♥️',
    crown: '👑',
    spade: '♤',
    diamond: '♦️',
    flag: '🎌',
    club: '♧'
};

// Audio files for sound effects
//const rollSound = new Audio('/assets/dice-roll.mp3');
//const placeSound = new Audio('/assets/dice-place.mp3');

// Storage keys
//const STORAGE_KEY = 'jhandiMundaGameState';

/**
 * The main component for the Jhandi Munda game.
 * It manages the game state, player bets, and the overall game flow.
 */
function JhandiMundaGame() {
    // State to keep track of the bets placed on each symbol
    const [bets, setBets] = useState({ club: 0, diamond: 0, heart: 0, spade: 0, flag: 0, crown: 0 });
    // State for the player's current balance
    const [balance, setBalance] = useState(10000);
    // State for the currently selected chip value for betting
    const [selectedChip, setSelectedChip] = useState(10);
    // State to track if the dice are currently rolling
    const [isRolling, setIsRolling] = useState(false);
    // State to display messages to the player (e.g., instructions, results)
    const [message, setMessage] = useState('Place your bets!');
    // State to track if the game is over
    const [gameOver, setGameOver] = useState(false);
    // Ref to access the DiceScene component's methods (e.g., to roll the dice)
    const diceSceneRef = useRef();
    // State to track unlocked achievements
    const [unlockedAchievements, setUnlockedAchievements] = useState([]);
    // State to manage the achievement popup
    const [achievementQueue, setAchievementQueue] = useState([]);
    const [currentAchievement, setCurrentAchievement] = useState(null);

    // State to enable/disable achievements
    const [achievementsEnabled, setAchievementsEnabled] = useState(true);

    // State to toggle between 3D physics dice and simple 2D dice mode
    // Defaults to 2D on small screens (mobile), 3D on larger screens
    const [use3DMode, setUse3DMode] = useState(() => window.innerWidth > 768);

    // State to hold the current simple dice results for rendering
    const [simpleDiceResults, setSimpleDiceResults] = useState(Array(6).fill(null));
    // State to track if simple dice are animating
    const [isSimpleRolling, setIsSimpleRolling] = useState(false);

    // New state for tracking statistics
    const [gamesPlayed, setGamesPlayed] = useState(0);
    const [consecutiveBets, setConsecutiveBets] = useState({ symbol: null, count: 0 });
    const [betsMadeInCurrentGame, setBetsMadeInCurrentGame] = useState(new Set());

    //Load game state from storage on mount
    // useEffect(() => {
    //     const loadGameState = async () => {
    //         try {
    //             const result = await window.storage.get(STORAGE_KEY);
    //             if (result && result.value) {
    //                 const savedState = JSON.parse(result.value);
    //                 setBalance(savedState.balance || 10000);
    //                 setUnlockedAchievements(savedState.unlockedAchievements || []);
    //                 setGamesPlayed(savedState.gamesPlayed || 0);
    //                 setConsecutiveBets(savedState.consecutiveBets || { symbol: null, count: 0 });
    //                 setAchievementsEnabled(savedState.achievementsEnabled !== undefined ? savedState.achievementsEnabled : true);
    //             }
    //         } catch (error) {
    //             // Key doesn't exist yet, use default values
    //             console.log('No saved game state found, starting fresh');
    //         }
    //     };
    //     loadGameState();
    // }, []);

    //Save game state to storage whenever important state changes
    // useEffect(() => {
    //     const saveGameState = async () => {
    //         const gameState = {
    //             balance,
    //             unlockedAchievements,
    //             gamesPlayed,
    //             consecutiveBets,
    //             achievementsEnabled
    //         };
    //         try {
    //             await window.storage.set(STORAGE_KEY, JSON.stringify(gameState));
    //         } catch (error) {
    //             console.error('Failed to save game state:', error);
    //         }
    //     };
    //     saveGameState();
    // }, [balance, unlockedAchievements, gamesPlayed, consecutiveBets, achievementsEnabled]);


    useEffect(() => {
        if (achievementQueue.length > 0 && !currentAchievement) {
            const nextAchievement = achievementQueue[0];
            setCurrentAchievement(nextAchievement);
            setAchievementQueue(prevQueue => prevQueue.slice(1));
        }
    }, [achievementQueue, currentAchievement]);

    useEffect(() => {
        if (currentAchievement) {
            const timer = setTimeout(() => {
                setCurrentAchievement(null);
            }, 3000); // Hide after 3 seconds
            return () => clearTimeout(timer);
        }
    }, [currentAchievement]);

    const unlockAchievement = (key) => {
        if (!unlockedAchievements.includes(key)) {
            if (achievementsEnabled) {
                setUnlockedAchievements([...unlockedAchievements, key]);
                setAchievementQueue(prevQueue => [...prevQueue, achievements[key]]);
            }            
        }
    };

    const handleAchievementsToggle = (e) => {
        setAchievementsEnabled(e.target.checked);
        if (!e.target.checked) {
            // Clear any pending achievement popups
            setCurrentAchievement(null);
            setAchievementQueue([]);
        }
    };

    /**
     * Handles placing a bet on a specific symbol.
     * @param {string} symbol - The symbol on which the bet is placed.
     */
    const handlePlaceBet = (symbol) => {
        // Prevent betting if dice are rolling or if the player can't afford the bet
        if (isRolling || balance < selectedChip) return;
        //placeSound.play();
        // Deduct the chip value from the balance
        setBalance(balance - selectedChip);
        // Add the chip value to the bet for the selected symbol
        setBets({ ...bets, [symbol]: bets[symbol] + selectedChip });

        setBetsMadeInCurrentGame(prev => new Set(prev.add(symbol)));

    };
    
    /**
     * Initiates the dice roll. Supports both 3D physics and simple 2D modes.
     */
    const handleRollDice = () => {
        const totalBet = Object.values(bets).reduce((sum, b) => sum + b, 0);
        // A bet must be placed before rolling the dice
        if (totalBet === 0) {
            setMessage('You must place a bet to roll!');
            return;
        }

        // Check bet amount achievements
        if (totalBet >= 10000) unlockAchievement('elephant');
        else if (totalBet >= 5000) unlockAchievement('tiger');
        else if (totalBet >= 1000) unlockAchievement('fish');

        // Check for "Confused Soul" achievement
        // if (!unlockedAchievements.includes('confusedSoul') && achievements.confusedSoul.check(null, null, bets)) {
        //     setUnlockedAchievements([...unlockedAchievements, 'confusedSoul']);
        //     setAchievementPopup(achievements.confusedSoul);
        // }

        if (achievements.confusedSoul.check(null, null, bets)) {
            unlockAchievement('confusedSoul');
        }

        //rollSound.play();
        setIsRolling(true);
        setMessage('Rolling...');

        if (use3DMode) {
            // 3D physics dice roll
            diceSceneRef.current.rollDice();
        } else {
            // Simple 2D dice roll - generate random results with animation delay
            setIsSimpleRolling(true);
            const results = Array(6).fill(null).map(() => symbols[Math.floor(Math.random() * symbols.length)]);
            
            // Show rolling animation for 1.5 seconds, then reveal results
            setTimeout(() => {
                setSimpleDiceResults(results);
                setIsSimpleRolling(false);
                handleRollComplete(results);
            }, 1500);
        }
    };

    /**
     * Handles the completion of the dice roll and calculates the results.
     * @param {string[]} results - An array of symbols that appeared on the dice.
     */
    const handleRollComplete = (results) => {
        let totalWinnings = 0;
        const counts = {};
        // Count the occurrences of each symbol in the results
        results.forEach(res => { counts[res] = (counts[res] || 0) + 1; });

        // Check for dice-based achievements
        // for (const key in achievements) {
        //     if (key !== 'emptyPockets' && key !== 'confusedSoul' && !unlockedAchievements.includes(key) && achievements[key].check(counts)) {
        //         const achievement = achievements[key];
        //         setUnlockedAchievements([...unlockedAchievements, key]);
        //         setAchievementPopup(achievement);
        //     }
        // }

        for (const key in achievements) {
            if (['unlucky', 'redExplosion', 'blackMagic', 'royalCourt', 'pair', 'threeOfAKind', 'fourOfAKind', 'fiveFlowers', 'perfectSix'].includes(key)) {
                if (achievements[key].check(counts)) {
                    unlockAchievement(key);
                }
            }
        }

        // Create a grouped dice result message, e.g., "Dice: Flag(🎌) x3, Club(♧) x2, Diamond(♦️) x1"
        const diceResultString = Object.entries(counts)
            .map(([symbol, count]) => `${symbol.charAt(0).toUpperCase() + symbol.slice(1)}(${symbolIcons[symbol]}) x${count}`)
            .join(', ');
        let resultMessage = `Dice: ${diceResultString}.`;

        let winDetails = [];
        let totalBet = 0;

        // Calculate winnings for each symbol
        for (const symbol in bets) {
            if (bets[symbol] > 0) {
                totalBet += bets[symbol];
                const matchCount = counts[symbol] || 0;
                
                if (matchCount > 1) {
                    // If a symbol appears more than once, the player wins.
                    // The winnings are the initial bet plus the bet amount multiplied by the number of matches.
                    const winAmount = bets[symbol] + (matchCount * bets[symbol]);
                    totalWinnings += winAmount;
                    winDetails.push(`You won ${symbol.charAt(0).toUpperCase() + symbol.slice(1)}(${symbolIcons[symbol]}) = ${matchCount} * ${bets[symbol]} + ${bets[symbol]} (Initial Bet) = ${winAmount}`);
                }
            }
        }
        
        const newBalance = balance + totalWinnings;
        const actualLoss = totalBet - totalWinnings;

        // Increment games played
        const newGamesPlayed = gamesPlayed + 1;
        setGamesPlayed(newGamesPlayed);

        // Check for stubborn gambler achievement
        if (betsMadeInCurrentGame.size === 1) {
            const singleSymbol = betsMadeInCurrentGame.values().next().value;
            if (consecutiveBets.symbol === singleSymbol) {
                const newCount = consecutiveBets.count + 1;
                setConsecutiveBets({ symbol: singleSymbol, count: newCount });

                if (newCount === 5) unlockAchievement('stubborn1');
                if (newCount === 10) unlockAchievement('stubborn2');
                if (newCount === 15) unlockAchievement('stubborn3');
            } else {
                setConsecutiveBets({ symbol: singleSymbol, count: 1 });
            }
        } else {
            setConsecutiveBets({ symbol: null, count: 0 });
        }

        // Check game count achievements
        if (newGamesPlayed === 10) unlockAchievement('tenGames');
        if (newGamesPlayed === 25) unlockAchievement('twentyFiveGames');
        if (newGamesPlayed === 50) unlockAchievement('fiftyGames');
        
        // Check winning achievements
        if (totalWinnings >= 10000) unlockAchievement('millionaireDreams');
        else if (totalWinnings >= 5000) unlockAchievement('sweetVictory');
        else if (totalWinnings >= 2000) unlockAchievement('smallJoy');

        // Check losing achievements (only if player actually lost money)
        if (totalWinnings === 0 && actualLoss > 0) {
            if (actualLoss >= 10000) unlockAchievement('bigLoser3');
            else if (actualLoss >= 5000) unlockAchievement('bigLoser2');
            else if (actualLoss >= 2000) unlockAchievement('bigLoser1');
        }


        if (totalWinnings > 0) {
            setMessage(`${resultMessage} ${winDetails.join('. ')}. You Bet: ${totalBet} You Won: ${totalWinnings}`);
            setBalance(newBalance);
        } else {
            setMessage(`${resultMessage} Better luck next time!`);
            setBalance(newBalance);
        }

        // Check for "Empty Pockets" achievement and trigger game over
        // if (newBalance === 0 && !unlockedAchievements.includes('emptyPockets')) {
        //     setUnlockedAchievements([...unlockedAchievements, 'emptyPockets']);
        //     setAchievementPopup(achievements.emptyPockets);
        //     setGameOver(true);
        //     setMessage('You lost! Would you like to play again?');
        // }
        if (newBalance === 0) {
            unlockAchievement('emptyPockets');
            setGameOver(true);
            setMessage('You lost! Would you like to play again?');
        }

        // Reset bets for the next round   
        setBets({ club: 0, diamond: 0, heart: 0, spade: 0, flag: 0, crown: 0 });
        setIsRolling(false);
        setBetsMadeInCurrentGame(new Set());
    };

    /**
     * Clears all placed bets and returns the bet amounts to the player's balance.
     */
    const clearBets = () => {
        if(isRolling) return;
        const totalBet = Object.values(bets).reduce((sum, b) => sum + b, 0);
        setBalance(balance + totalBet);
        setBets({ club: 0, diamond: 0, heart: 0, spade: 0, flag: 0, crown: 0 });
        setMessage('Bets cleared. Place new bets!');
    }

    const playAgain = () => {
        setBalance(10000);
        setGameOver(false);
        setGamesPlayed(0);
        setConsecutiveBets({ symbol: null, count: 0 });
        setMessage('Place your bets!');
    }

    /**
     * Handles toggling between 3D and 2D dice modes.
     */
    const handleDiceModeToggle = useCallback(() => {
        setUse3DMode(prev => !prev);
    }, []);

    return (
        <div className="game-container">
            {currentAchievement && (
                <div className="achievement-popup">
                    <h3>Achievement Unlocked!</h3>
                    <p>{currentAchievement.title}</p>
                    <p>{currentAchievement.description}</p>
                </div>
            )}
            <dialog open={gameOver} className="game-over-dialog">
                <h2>You lost!</h2>
                <p>Would you like to play again?</p>
                <button onClick={playAgain} aria-label="Play again">Play Again</button>
            </dialog>
            {/* Dice scene - conditionally render 3D or 2D mode */}
            <div className="scene-container">
                {use3DMode ? (
                    <DiceScene ref={diceSceneRef} onRollComplete={handleRollComplete} />
                ) : (
                    <SimpleDiceRoll 
                        results={simpleDiceResults} 
                        isRolling={isSimpleRolling} 
                    />
                )}
            </div>
            {/* The user interface for betting and game controls */}
            <div className="ui-container">
                {/* Settings toggles row */}
                <div className="settings-toggles">
                    <label className="toggle-label" aria-label="Toggle 3D dice mode">
                        <span className="toggle-text">{use3DMode ? '3D Dice' : '2D Dice'}</span>
                        <div className={`toggle-switch ${use3DMode ? 'active' : ''}`} onClick={handleDiceModeToggle}>
                            <div className="toggle-knob"></div>
                        </div>
                    </label>
                    <label className="toggle-label" aria-label="Toggle achievements">
                        <span className="toggle-text">Achievements</span>
                        <div className={`toggle-switch ${achievementsEnabled ? 'active' : ''}`} onClick={(e) => handleAchievementsToggle({ target: { checked: !achievementsEnabled }})}>
                            <div className="toggle-knob"></div>
                        </div>
                    </label>
                </div>
                <div className="info-bar">
                    <h2>Balance: ${balance}</h2>
                    <p className="message">{message}</p>
                </div>

                {/* The betting board where players place their bets */}
                <div className="betting-board">
                    {symbols.map(symbol => (
                        <div 
                            key={symbol} 
                            className={`betting-square ${bets[symbol] > 0 ? 'has-bet' : ''}`} 
                            onClick={() => handlePlaceBet(symbol)}
                            aria-label={`Bet on ${symbol}`}
                        >
                            <div className="symbol-container">
                                <img src={`./assets/${symbol}.png`} alt={symbol} className="symbol-icon" />
                                <span className="symbol-text">{symbol.charAt(0).toUpperCase() + symbol.slice(1)}</span>
                            </div>
                            {bets[symbol] > 0 && <div className="bet-amount">${bets[symbol]}</div>}
                        </div>
                    ))}
                </div>

                {/* Game controls for selecting chips and rolling the dice */}
                <div className="controls">
                    <div className="chips">
                        <p>Select Chip:</p>
                        {[10, 50, 100, 500, 1000].map(chipValue => (
                            <button 
                                key={chipValue}
                                className={`chip ${selectedChip === chipValue ? 'selected' : ''}`}
                                onClick={() => setSelectedChip(chipValue)}
                                aria-label={`Select ${chipValue} chip`}
                            >
                                ${chipValue}
                            </button>
                        ))}
                    </div>
                    <div className="action-buttons">
                        <button onClick={handleRollDice} disabled={isRolling} className="roll-button" aria-label="Roll dice">
                            {isRolling ? 'Rolling...' : 'Roll Dice'}
                        </button>
                        <button onClick={clearBets} disabled={isRolling} className="clear-button" aria-label="Clear all bets">Clear Bets</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default JhandiMundaGame;
